import { create } from 'zustand'

import { type LibraryChatTurn, type LibraryCitation, postLibraryChat } from '@/api/services/book-ai-purpose'

export type LibraryChatMessage = {
  id: number
  role: 'user' | 'assistant'
  content: string
  /** 回答の根拠にした本文 */
  citations: LibraryCitation[]
  /** 質問で見つかった本文すべて（近い順） */
  passages: LibraryCitation[]
}

/**
 * 本棚全体（AI 解析済みのすべての本）への質問
 *
 * 履歴はサーバーに保存せず、開いている間だけ持つ（本を移っても続けられる）。
 */
type LibraryChatStore = {
  messages: LibraryChatMessage[]
  isSending: boolean
  send: (question: string) => Promise<boolean>
  clear: () => void
}

// 直前の会話として送る数（サーバー側の上限と同じ）
const HISTORY_TURNS = 6

let nextId = 1

export const useLibraryChatStore = create<LibraryChatStore>((set, get) => ({
  messages: [],
  isSending: false,

  send: async (question) => {
    if (get().isSending) return false
    const history: LibraryChatTurn[] = get()
      .messages.slice(-HISTORY_TURNS)
      .map(({ role, content }) => ({ role, content }))
    const asked: LibraryChatMessage = { id: nextId++, role: 'user', content: question, citations: [], passages: [] }
    set((state) => ({ messages: [...state.messages, asked], isSending: true }))
    try {
      const res = await postLibraryChat(question, history)
      const answer: LibraryChatMessage = {
        id: nextId++,
        role: 'assistant',
        content: res.answer,
        citations: res.citations,
        passages: res.passages,
      }
      set((state) => ({ messages: [...state.messages, answer] }))
      return true
    } catch (error) {
      console.error('本棚への質問を送れませんでした:', error)
      // 送れなかった質問は消して、入力欄に戻せるようにする
      set((state) => ({ messages: state.messages.filter((m) => m.id !== asked.id) }))
      return false
    } finally {
      set({ isSending: false })
    }
  },

  clear: () => set({ messages: [] }),
}))
